import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, Camera, Building, Mail, Phone, MapPin, Award } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthContext';
type ProfileFormData = {
  name: string;
  companyName: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  website?: string;
  registrationNumber?: string;
};
export function Profile() {
  const {
    user,
    login
  } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: user?.name || '',
      companyName: user?.companyName || '',
      email: user?.email || '',
      phone: '+60 12-345-6789',
      address: '123 Jalan Bukit Bintang, Kuala Lumpur',
      description: 'Professional home service provider specializing in painting, waterproofing, and micro-repairs.',
      website: 'www.example.com',
      registrationNumber: 'REG12345678' // Default value
    }
  });
  const onSubmit = (data: ProfileFormData) => {
    // Update user info in context and localStorage
    const updatedUser = {
      ...user!,
      name: data.name,
      companyName: data.companyName,
      email: data.email
      // In a real app, you would update other fields in the user object
    };
    login(updatedUser);
    // Simulate API call delay
    setTimeout(() => {
      setIsEditing(false);
    }, 500);
  };
  return <div data-id="element-1942">
      <div className="mb-6" data-id="element-1943">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1944">Vendor Profile</h1>
        <p className="text-gray-600 mt-1" data-id="element-1945">Manage your business information</p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-1946">
        <div className="p-6 sm:p-8" data-id="element-1947">
          <div className="flex flex-col md:flex-row md:items-center" data-id="element-1948">
            <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6" data-id="element-1949">
              <div className="relative" data-id="element-1950">
                <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary" data-id="element-1951">
                  {user?.companyName?.charAt(0) || user?.name?.charAt(0) || 'V'}
                </div>
                <button className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center shadow-md" data-id="element-1952">
                  <Camera size={16} data-id="element-1953" />
                </button>
              </div>
            </div>
            <div className="flex-1" data-id="element-1954">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1955">
                {user?.companyName || user?.name}
              </h2>
              <p className="text-gray-600 mt-1" data-id="element-1956">{user?.email}</p>
              <div className="mt-3 flex items-center" data-id="element-1957">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1958">
                  <Award size={14} className="mr-1" data-id="element-1959" />
                  Certified Vendor
                </span>
                <span className="ml-2 text-sm text-gray-500" data-id="element-1960">
                  Since October 2023
                </span>
              </div>
            </div>
            {!isEditing && <div className="mt-4 md:mt-0" data-id="element-1961">
                <button onClick={() => setIsEditing(true)} className="px-4 py-2 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors" data-id="element-1962">
                  Edit Profile
                </button>
              </div>}
          </div>
          {isEditing ? <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6" data-id="element-1963">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1964">
                <div data-id="element-1965">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-1966">
                    Full Name
                  </label>
                  <div className="mt-1 relative" data-id="element-1967">
                    <input id="name" type="text" className={`block w-full px-4 py-3 rounded-md border ${errors.name ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('name', {
                  required: 'Full name is required'
                })} data-id="element-1968" />
                    {errors.name && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1969">
                        <AlertCircle className="h-5 w-5 text-red-500" data-id="element-1970" />
                      </div>}
                  </div>
                  {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-1971">
                      {errors.name.message}
                    </p>}
                </div>
                <div data-id="element-1972">
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700" data-id="element-1973">
                    Company Name
                  </label>
                  <div className="mt-1 relative" data-id="element-1974">
                    <input id="companyName" type="text" className={`block w-full px-4 py-3 rounded-md border ${errors.companyName ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('companyName', {
                  required: 'Company name is required'
                })} data-id="element-1975" />
                    {errors.companyName && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1976">
                        <AlertCircle className="h-5 w-5 text-red-500" data-id="element-1977" />
                      </div>}
                  </div>
                  {errors.companyName && <p className="mt-1 text-sm text-red-600" data-id="element-1978">
                      {errors.companyName.message}
                    </p>}
                </div>
                <div data-id="element-1979">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-1980">
                    Email Address
                  </label>
                  <div className="mt-1 relative" data-id="element-1981">
                    <input id="email" type="email" className={`block w-full px-4 py-3 rounded-md border ${errors.email ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })} data-id="element-1982" />
                    {errors.email && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1983">
                        <AlertCircle className="h-5 w-5 text-red-500" data-id="element-1984" />
                      </div>}
                  </div>
                  {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-1985">
                      {errors.email.message}
                    </p>}
                </div>
                <div data-id="element-1986">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700" data-id="element-1987">
                    Phone Number
                  </label>
                  <div className="mt-1 relative" data-id="element-1988">
                    <input id="phone" type="text" className={`block w-full px-4 py-3 rounded-md border ${errors.phone ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary`} {...register('phone', {
                  required: 'Phone number is required'
                })} data-id="element-1989" />
                    {errors.phone && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1990">
                        <AlertCircle className="h-5 w-5 text-red-500" data-id="element-1991" />
                      </div>}
                  </div>
                  {errors.phone && <p className="mt-1 text-sm text-red-600" data-id="element-1992">
                      {errors.phone.message}
                    </p>}
                </div>
                <div className="md:col-span-2" data-id="element-1993">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700" data-id="element-1994">
                    Business Address
                  </label>
                  <div className="mt-1 relative" data-id="element-1995">
                    <input id="address" type="text" className={`block w-full px-4 py-3 rounded-md border ${errors.address ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary`} {...register('address', {
                  required: 'Address is required'
                })} data-id="element-1996" />
                    {errors.address && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1997">
                        <AlertCircle className="h-5 w-5 text-red-500" data-id="element-1998" />
                      </div>}
                  </div>
                  {errors.address && <p className="mt-1 text-sm text-red-600" data-id="element-1999">
                      {errors.address.message}
                    </p>}
                </div>
                <div className="md:col-span-2" data-id="element-2000">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2001">
                    Business Description
                  </label>
                  <div className="mt-1 relative" data-id="element-2002">
                    <textarea id="description" rows={4} className={`block w-full px-4 py-3 rounded-md border ${errors.description ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('description', {
                  required: 'Description is required'
                })} data-id="element-2003" />
                  </div>
                  {errors.description && <p className="mt-1 text-sm text-red-600" data-id="element-2004">
                      {errors.description.message}
                    </p>}
                </div>
                <div data-id="element-2005">
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700" data-id="element-2006">
                    Website (Optional)
                  </label>
                  <div className="mt-1 relative" data-id="element-2007">
                    <input id="website" type="text" className="block w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-primary focus:border-primary" {...register('website')} data-id="element-2008" />
                  </div>
                </div>
                <div data-id="element-2009">
                  <label htmlFor="registrationNumber" className="block text-sm font-medium text-gray-700" data-id="element-2010">
                    Business Registration Number
                  </label>
                  <div className="mt-1 relative" data-id="element-2011">
                    <input id="registrationNumber" type="text" className="block w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-primary focus:border-primary" {...register('registrationNumber')} data-id="element-2012" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-4" data-id="element-2013">
                <button type="button" onClick={() => setIsEditing(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2014">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-70" data-id="element-2015">
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form> : <div className="mt-8 border-t border-gray-200 pt-8" data-id="element-2016">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2017">
                <div data-id="element-2018">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2019">
                    Contact Information
                  </h3>
                  <div className="mt-4 space-y-3" data-id="element-2020">
                    <div className="flex items-start" data-id="element-2021">
                      <Building size={20} className="text-gray-500 mt-0.5 mr-3" data-id="element-2022" />
                      <div data-id="element-2023">
                        <p className="text-sm font-medium text-gray-700" data-id="element-2024">
                          Company
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-2025">
                          {user?.companyName || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-2026">
                      <Mail size={20} className="text-gray-500 mt-0.5 mr-3" data-id="element-2027" />
                      <div data-id="element-2028">
                        <p className="text-sm font-medium text-gray-700" data-id="element-2029">
                          Email
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-2030">{user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-2031">
                      <Phone size={20} className="text-gray-500 mt-0.5 mr-3" data-id="element-2032" />
                      <div data-id="element-2033">
                        <p className="text-sm font-medium text-gray-700" data-id="element-2034">
                          Phone
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-2035">+60 12-345-6789</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-2036">
                      <MapPin size={20} className="text-gray-500 mt-0.5 mr-3" data-id="element-2037" />
                      <div data-id="element-2038">
                        <p className="text-sm font-medium text-gray-700" data-id="element-2039">
                          Address
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-2040">
                          123 Jalan Bukit Bintang, Kuala Lumpur
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-2041">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2042">
                    Business Information
                  </h3>
                  <div className="mt-4" data-id="element-2043">
                    <p className="text-sm text-gray-600" data-id="element-2044">
                      Professional home service provider specializing in
                      painting, waterproofing, and micro-repairs. Our team of
                      certified professionals delivers high-quality services
                      with warranty coverage.
                    </p>
                    <div className="mt-4" data-id="element-2045">
                      <p className="text-sm font-medium text-gray-700" data-id="element-2046">
                        Business Registration
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2047">REG12345678</p>
                    </div>
                    <div className="mt-2" data-id="element-2048">
                      <p className="text-sm font-medium text-gray-700" data-id="element-2049">
                        Website
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2050">www.example.com</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}